/**
 * canvas-engine/presets/decompositionAdapters/dynamic.js
 *
 * Dynamic_Data_Section family adapter (products / courses / bookings / cart).
 *
 * A dynamic section is authored chrome (headings, labels, styling, spacing,
 * default imagery) PLUS a live list bound at render time. The core engine
 * already decomposes the authored chrome into individually editable
 * Canvas_Elements and — when the descriptor carries a `binding: { source,
 * params }` marker — emits a `dataBound` Canvas_Element for the live list, so
 * this adapter delegates to `buildSectionCanvas` and then GUARANTEES the
 * live-data contract: at least one `dataBound` element is present so authors
 * never hand-author the live list items (Req 25.1, 25.4).
 *
 * The adapter accepts EITHER a transcribed `DYNAMIC_PRESETS` descriptor OR a
 * section-type string looked up in `DYNAMIC_PRESETS` (either the bare key
 * `products`/`courses`/`bookings`/`cart` or its `dynamic*` sectionType). It
 * lays out at the fixed `Reference_Width` with `idPrefix = sectionType`.
 *
 * ZERO-REUSE CONSTRAINT: imports ONLY sibling engine modules + transcribed
 * presetData VALUES. Pure, deterministic. Never emits a `native` element.
 *
 * Requirements: 25.1, 25.4
 */

import buildSectionCanvas, { dataBoundElement, REFERENCE_FRAME_WIDTH } from './buildSectionCanvas';
import { resolvePreset, adapterOptions, handledTypesOf } from './resolvePreset';
import { normalizeCanvas } from '../../model/canvasModel';
import { MIN_DIMENSION } from '../../model/schema';
import { DYNAMIC_PRESETS } from '../presetData/dynamicPresets';

function isPlainObject(v) {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function pickString(v) {
  return typeof v === 'string' ? v.trim() : '';
}

/** The Dynamic_Data_Section types this adapter handles (keys + sectionTypes). */
export const DYNAMIC_TYPES = Object.freeze(handledTypesOf(DYNAMIC_PRESETS));

/** True when `sectionType` is decomposed by the dynamic adapter. */
export function handlesDynamicType(sectionType) {
  const t = typeof sectionType === 'string' ? sectionType.trim() : '';
  return DYNAMIC_TYPES.includes(t) || (t.length >= 7 && t.slice(0, 7) === 'dynamic');
}

/** Resolve the binding marker from a dynamic descriptor (top-level or content). */
function resolveBinding(data) {
  if (isPlainObject(data.binding) && pickString(data.binding.source)) return data.binding;
  const content = isPlainObject(data.content) ? data.content : {};
  if (isPlainObject(content.binding) && pickString(content.binding.source)) return content.binding;
  return null;
}

/**
 * Decompose a Dynamic_Data_Section into a normalized Canvas_Model: editable
 * authored chrome PLUS a `dataBound` element carrying the binding.
 *
 * @param {(string|object)} sectionTypeOrData a `DYNAMIC_PRESETS` key /
 *   sectionType, or a transcribed dynamic preset DATA descriptor
 * @param {object} [options] `buildSectionCanvas` options
 * @returns {object} a normalized Canvas_Model
 */
export function dynamicToCanvas(sectionTypeOrData, options = {}) {
  const { data, sectionType } = resolvePreset(sectionTypeOrData, DYNAMIC_PRESETS, options);
  const opts = adapterOptions(sectionType, REFERENCE_FRAME_WIDTH, options);
  const canvas = buildSectionCanvas(data, opts);

  // The core engine emits a dataBound when a binding marker is present; in the
  // normal case nothing more is required.
  if (canvas.elements.some((el) => el.type === 'dataBound')) {
    return canvas;
  }

  // Defensive fallback: a dynamic section must always expose a live-data
  // region. If no binding marker reached the core engine, append a dataBound
  // element so the live list is never hand-authored (Req 25.4).
  const binding = resolveBinding(data);
  const content = isPlainObject(data.content) ? data.content : {};
  const frameWidth = Number.isFinite(opts.frameWidth) && opts.frameWidth >= MIN_DIMENSION ? opts.frameWidth : REFERENCE_FRAME_WIDTH;
  const idPrefix = pickString(opts.idPrefix) || sectionType;

  const source = binding ? pickString(binding.source) : pickString(data.sectionType) || 'items';
  const params = binding && isPlainObject(binding.params) ? { ...binding.params } : {};

  const dbContent = {
    title: pickString(content.emptyStateTitle) || pickString(content.heading) || pickString(data.label),
    subtitle: pickString(content.subheading) || pickString(content.subtitle),
  };
  const y = Math.max(canvas.height - 420, 0);
  const element = dataBoundElement(
    `${idPrefix}-dataBound-fallback`,
    { x: 24, y, width: Math.max(frameWidth - 48, MIN_DIMENSION), height: 420 },
    canvas.elements.length + 1,
    dbContent,
    { source, params },
  );

  return normalizeCanvas({ ...canvas, height: y + 420 + 24, elements: [...canvas.elements, element] });
}

export default dynamicToCanvas;
